import { Grid } from '@mui/material';
import { RoutalAvatar, RoutalSelect, RoutalSelectMultiSelectOption, VSpacer } from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

import {
  drivers as barcelonaDrivers,
  stops as barcelonaStops,
} from '../../../../assets/templates/examples/locations/Barcelona';
import { FlatProject } from '../../../../domain/project/FlatProject';
import { useRetrieveOrganization } from '../../../../hooks/organization/useRetrieveOrganization';
import { useRetrieveActiveProject } from '../../../../hooks/project/useRetrieveActiveProject';
import { useRetrieveFlatProjects } from '../../../../hooks/project/useRetrieveFlatProjects';
import { useRetrieveProjectUsers } from '../../../../hooks/project/useRetrieveProjectUsers';
import { useRetrieveSupportDocs } from '../../../../hooks/support-docs/useRetrieveSupportDocs';
import { useRetrieveUser } from '../../../../hooks/user/useRetrieveUser';
import { useRetrieveUserCredentials } from '../../../../hooks/user/useRetrieveUserCredentials';
import Button from '../../../../react-components/Button';
import FormattedMessage from '../../../../react-components/FormattedCustomMessage';
import Text from '../../../../react-components/Text';
import configuration from '../../../../services/configuration';
import { getUploadAsBase64 } from '../../../../services/requests';
import { UpgradeIntegrations } from '../common/UpgradeIntegrations';

const TitleTextStyles = { cursor: `default`, fontSize: `16px` };

const BorderedContainer = styled.div`
  border: 1px solid #aaaaaa;
  border-radius: 2px;
  padding: 14px;
`;

const CodeStyled = styled.span`
  color: #9d9c9e;
  background-color: #e9e9e9;
  padding: 0px 9px;
`;

type IExampleData = {
  project?: FlatProject;
  numberOfDrivers: number;
  numberOfStops: number;
};

export const ApiKeys = () => {
  const { retrieveDoc } = useRetrieveSupportDocs();
  const { data: organization } = useRetrieveOrganization();
  const { data: userCredentials } = useRetrieveUserCredentials();
  const { data: user } = useRetrieveUser();
  const { data: activeProject } = useRetrieveActiveProject();
  const { data: projectUsers } = useRetrieveProjectUsers({ project: activeProject! });
  const { data: projects } = useRetrieveFlatProjects({ user });
  const [selectedProjectId, setSelectedProjectId] = useState<string>(``);

  const [exampleData, setExampleData] = useState<IExampleData>({
    project: undefined,
    numberOfDrivers: 1,
    numberOfStops: 1,
  });

  useEffect(() => {
    if (projects && projects.length > 0) {
      setSelectedProjectId(projects[0].id);
    }
    if (activeProject) {
      setExampleData({
        ...exampleData,
        project: FlatProject.create(
          {
            label: activeProject.label,
            avatar_url: activeProject.avatar_url,
            description: activeProject.description,
            users: projectUsers?.length ?? 0,
          },
          activeProject.id
        ),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProject, projects]);

  const getSelectedProject = () => {
    const selectedProject = projects?.find((project) => project.id === selectedProjectId);
    return selectedProject;
  };

  const getQueryExample = () => {
    const planData = {
      label: `example_plan`,
      drivers: Array(exampleData.numberOfDrivers)
        .fill(``)
        .map((_, index) => ({
          label: barcelonaDrivers.drivers.label[index],
          start_location: {
            label: barcelonaDrivers.drivers.start_address[index],
            lat: barcelonaDrivers.drivers.start_address_coordinates![index].lat,
            lng: barcelonaDrivers.drivers.start_address_coordinates![index].lng,
          },
          end_location: {
            label: barcelonaDrivers.drivers.end_address[index],
            lat: barcelonaDrivers.drivers.end_address_coordinates![index].lat,
            lng: barcelonaDrivers.drivers.end_address_coordinates![index].lng,
          },
        })),
      stops: Array(exampleData.numberOfStops)
        .fill(``)
        .map((_, index) => ({
          label: barcelonaStops.stops.delivery_label[index],
          location: {
            label: barcelonaStops.stops.delivery_address[index],
            lat: barcelonaStops.stops.delivery_address_coordinates![index].lat,
            lng: barcelonaStops.stops.delivery_address_coordinates![index].lng,
          },
          ...(barcelonaStops.stops.delivery_address_details[index] !== ``
            ? { location_details: barcelonaStops.stops.delivery_address_details[index] }
            : {}),
        })),
    };

    const url = `'${configuration.PLANNER_BACK_END}/v2/plan?private_key=${user?.private_key}&project_id=${exampleData.project?.id}'`;
    const body = JSON.stringify(planData);
    return `curl -X POST -H 'Content-Type: application/json' -d '${body}' ${url}`;
  };

  if (!userCredentials?.subscription.test(`planner.api`)) {
    return <UpgradeIntegrations />;
  }

  return (
    <Grid
      container
      direction="column"
      wrap="nowrap"
      style={{
        padding: `16px 22px`,
        backgroundColor: `white`,
        zIndex: 2,
        height: `100%`,
        overflowY: `auto`,
      }}
    >
      <Grid container direction="column" wrap="nowrap">
        <Grid container direction="column" style={{ padding: `16px 22px` }}>
          <Grid item container direction="row" justifyContent="space-between">
            <Grid item>
              <Text variant="h6">
                <FormattedMessage
                  id="settings.organization.projects.title"
                  values={{ organizationName: organization?.label }}
                />
              </Text>
            </Grid>

            <Grid item>
              <Button
                styleType="secondary"
                intlId="settings.developers.api_keys.visit_developers_site"
                onClick={() =>
                  window.open(retrieveDoc({ supportPage: `developers`, language: user?.language.value }), `_blank`)
                }
              />
            </Grid>
          </Grid>

          <VSpacer />

          <Text variant="body">
            <FormattedMessage id="settings.developers.api_keys.description" />
          </Text>

          <VSpacer />

          <Grid item>
            <BorderedContainer>
              <Grid item container direction="column">
                <Grid item>
                  <Text
                    variant="body"
                    tagName="span"
                    style={TitleTextStyles}
                    intlId="settings.developers.api_keys.private_key"
                  />
                </Grid>

                <VSpacer small />

                <Grid item>
                  <CodeStyled>{user?.private_key ?? ``}</CodeStyled>
                </Grid>
              </Grid>
            </BorderedContainer>

            <VSpacer />

            <BorderedContainer>
              <Grid container direction="row" wrap="nowrap">
                <Grid item container direction="column">
                  <Grid item>
                    <Text
                      variant="body"
                      tagName="span"
                      style={TitleTextStyles}
                      intlId="settings.developers.api_keys.project_id"
                    />
                  </Grid>

                  <VSpacer small />

                  <Grid item>
                    <CodeStyled>{selectedProjectId}</CodeStyled>
                  </Grid>
                </Grid>

                <Grid item container direction="column" alignItems="flex-end">
                  <RoutalSelect
                    id="select-project"
                    containerStyle={{
                      width: `300px`,
                    }}
                    style={{ backgroundColor: `#E9E9E9`, color: `#707070`, padding: `4px 4px` }}
                    popoverProps={{
                      options: (projects ?? []).map(
                        (project): RoutalSelectMultiSelectOption => ({
                          key: `project-${project.id}`,
                          width: 300,
                          content: (
                            <Grid container alignItems="center" wrap="nowrap">
                              <RoutalAvatar
                                url={project.avatar_url}
                                getUploadAsBase64={getUploadAsBase64}
                                label={project.label}
                                labelStyles={{ fontSize: `10px`, lineHeight: 2 }}
                                containerStyles={{ width: `24px`, height: `24px` }}
                              />
                              <Grid
                                item
                                style={{
                                  whiteSpace: `nowrap`,
                                  textOverflow: `ellipsis`,
                                  overflow: `hidden`,
                                  width: `252px`,
                                }}
                              >
                                <span style={{ marginLeft: `8px` }}>{project.label ?? ``}</span>
                              </Grid>
                            </Grid>
                          ),
                          onClick: () => {
                            setSelectedProjectId(project.id);
                          },
                        })
                      ),
                    }}
                  >
                    <Grid container alignItems="center" wrap="nowrap">
                      <RoutalAvatar
                        url={getSelectedProject()?.avatar_url}
                        getUploadAsBase64={getUploadAsBase64}
                        label={getSelectedProject()?.label}
                        labelStyles={{ fontSize: `10px`, lineHeight: 2 }}
                        containerStyles={{ width: `24px`, height: `24px` }}
                      />
                      <Grid
                        item
                        style={{
                          whiteSpace: `nowrap`,
                          textOverflow: `ellipsis`,
                          overflow: `hidden`,
                          width: `172px`,
                          textAlign: `start`,
                        }}
                      >
                        <span style={{ marginLeft: `8px` }}>{getSelectedProject()?.label ?? ``}</span>
                      </Grid>
                    </Grid>
                  </RoutalSelect>
                </Grid>
              </Grid>
            </BorderedContainer>

            <VSpacer />

            {exampleData ? (
              <BorderedContainer style={{ backgroundColor: `#E9E9E9`, borderColor: `#E9E9E9` }}>
                <Grid item container direction="column">
                  <Grid item>
                    <Text variant="body" style={TitleTextStyles} intlId="settings.developers.api_keys.example" />
                  </Grid>

                  <VSpacer small />

                  <Grid item container direction="row" alignItems="center">
                    <Grid item>
                      <Text variant="body" intlId="settings.developers.api_keys.example.text_1" />
                    </Grid>

                    <Grid item>
                      <RoutalSelect
                        id="example-project"
                        style={{
                          backgroundColor: `transparent`,
                        }}
                        containerStyle={{
                          width: `300px`,
                          padding: `4px 8px`,
                        }}
                        popoverProps={{
                          options: (projects ?? []).map(
                            (project): RoutalSelectMultiSelectOption => ({
                              key: `project-${project.id}`,
                              width: 300,
                              content: (
                                <Grid container alignItems="center" wrap="nowrap">
                                  <RoutalAvatar
                                    url={project.avatar_url}
                                    getUploadAsBase64={getUploadAsBase64}
                                    label={project.label}
                                    labelStyles={{ fontSize: `10px`, lineHeight: 2 }}
                                    containerStyles={{ width: `24px`, height: `24px` }}
                                  />
                                  <Grid
                                    item
                                    style={{
                                      whiteSpace: `nowrap`,
                                      textOverflow: `ellipsis`,
                                      overflow: `hidden`,
                                      width: `252px`,
                                    }}
                                  >
                                    <span style={{ marginLeft: `8px` }}>{project.label ?? ``}</span>
                                  </Grid>
                                </Grid>
                              ),
                              onClick: () => {
                                setExampleData({
                                  ...exampleData,
                                  project,
                                });
                              },
                            })
                          ),
                        }}
                      >
                        <Grid container alignItems="center" wrap="nowrap">
                          <RoutalAvatar
                            url={exampleData.project?.avatar_url}
                            getUploadAsBase64={getUploadAsBase64}
                            label={exampleData.project?.label}
                            labelStyles={{ fontSize: `10px`, lineHeight: 2 }}
                            containerStyles={{ width: `24px`, height: `24px` }}
                          />
                          <Grid
                            item
                            style={{
                              whiteSpace: `nowrap`,
                              textOverflow: `ellipsis`,
                              overflow: `hidden`,
                              textAlign: `start`,
                            }}
                          >
                            <Text isBold style={{ cursor: `pointer`, marginLeft: `8px` }}>
                              {exampleData.project?.label ?? ``}
                            </Text>
                          </Grid>
                        </Grid>
                      </RoutalSelect>
                    </Grid>

                    <Grid item>
                      <Text variant="body" intlId="settings.developers.api_keys.example.text_2" />
                    </Grid>

                    <Grid item>
                      <RoutalSelect
                        id="number-of-drivers"
                        style={{
                          justifyContent: `space-between`,
                        }}
                        containerStyle={{
                          width: `120px`,
                          padding: `4px 8px`,
                        }}
                        popoverProps={{
                          options: Array.from({ length: barcelonaDrivers.drivers.label.length }, (x, i) => i + 1).map(
                            (num: number): RoutalSelectMultiSelectOption => ({
                              key: `number-of-drivers-${num}`,
                              width: 220,
                              content: (
                                <Text
                                  variant="body"
                                  intlId="settings.developers.api_keys.example.num_of_drivers"
                                  intlValues={{ num }}
                                />
                              ),
                              onClick: () => {
                                setExampleData({
                                  ...exampleData,
                                  numberOfDrivers: num,
                                });
                              },
                            })
                          ),
                        }}
                      >
                        <Text
                          isBold
                          style={{ cursor: `pointer` }}
                          intlId="settings.developers.api_keys.example.num_of_drivers"
                          intlValues={{ num: exampleData.numberOfDrivers }}
                        />
                      </RoutalSelect>
                    </Grid>

                    <Grid item>
                      <Text variant="body" intlId="settings.developers.api_keys.example.text_3" />
                    </Grid>

                    <Grid item>
                      <RoutalSelect
                        id="number-of-stops"
                        style={{
                          justifyContent: `space-between`,
                        }}
                        containerStyle={{
                          width: `120px`,
                          padding: `4px 8px`,
                        }}
                        popoverProps={{
                          options: Array.from({ length: barcelonaStops.stops.label.length }, (x, i) => i + 1).map(
                            (num: number): RoutalSelectMultiSelectOption => ({
                              key: `number-of-stops-${num}`,
                              width: 220,
                              content: (
                                <Text
                                  variant="body"
                                  style={{ cursor: `pointer` }}
                                  intlId="settings.developers.api_keys.example.num_of_stops"
                                  intlValues={{ num }}
                                />
                              ),
                              onClick: () => {
                                setExampleData({
                                  ...exampleData,
                                  numberOfStops: num,
                                });
                              },
                            })
                          ),
                        }}
                      >
                        <Text
                          variant="body"
                          style={{ cursor: `pointer` }}
                          intlId="settings.developers.api_keys.example.num_of_stops"
                          intlValues={{ num: exampleData.numberOfStops }}
                        />
                      </RoutalSelect>
                    </Grid>
                  </Grid>

                  <VSpacer />

                  <Grid
                    item
                    style={{ backgroundColor: `#333940`, minHeight: `400px`, color: `#e9e9e9`, padding: `4px 8px` }}
                  >
                    {/* <SyntaxHighlighter wrapLines={false} wrapLongLines={false} useInlineStyles language="javascript" style={highlightTheme} customStyle={{ backgroundColor: `transparent` }}>
                    {getQueryExample()}
                  </SyntaxHighlighter> */}
                    {/* <pre> */}
                    <pre style={{ whiteSpace: `pre-wrap` }}>
                      <code
                        style={{
                          fontFamily: `Consolas, "Andale Mono WT", "Andale Mono", "Lucida Console", "Lucida Sans Typewriter", "DejaVu Sans Mono", "Bitstream Vera Sans Mono", "Liberation Mono", "Nimbus Mono L", Monaco, "Courier New", Courier, monospace`,
                        }}
                      >
                        {getQueryExample()}
                      </code>
                    </pre>
                  </Grid>
                </Grid>
              </BorderedContainer>
            ) : null}
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};
